/**
 * Bottom sheets for the planogram order tool.
 *
 * Sheets slide up from the bottom so every control lands in the thumb zone,
 * which matters because this tool is used one-handed in front of a rack.
 */

import React, { useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  X,
  MapPin,
  Tag,
  Layers,
  AlertTriangle,
  HelpCircle,
  Trash2,
  Copy,
  Download,
} from 'lucide-react';
import { PlanogramProduct, PlanogramSlot } from '../../@types/planogram';
import {
  BorderRadius,
  Shadows,
  Spacing,
  ThemeColors,
  Typography,
  ZIndex,
} from '../../styles/ThemeStyles';
import { FORM_COLORS, FORM_LABELS, formatPrice, positionLabel } from './planogramTheme';
import { QtyStepper } from './PlanogramPanelViews';

/** Common bulk quantities, so the usual case is one tap instead of many. */
const QUICK_QUANTITIES = [6, 12, 24, 48];

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.modal};
  background: ${ThemeColors.overlay};
  animation: ${fadeIn} 160ms ease-out;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.xl} ${BorderRadius.xl} 0 0;
  box-shadow: ${Shadows.xl};
  animation: ${slideUp} 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const Grabber = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 4px;
  margin: ${Spacing.sm} auto 0;
  border-radius: 2px;
  background: ${ThemeColors.border.medium};
`;

const SheetHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: ${BorderRadius.round};
  background: ${ThemeColors.background.default};
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
`;

const SheetBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: ${Spacing.md};
`;

const SheetFoot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.background.paper};
`;

interface BottomSheetProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const SheetTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  line-height: 1.25;
`;

const SheetSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
`;

export const BottomSheet: React.FC<BottomSheetProps> = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <Backdrop onClick={onClose} role="dialog" aria-modal="true">
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Grabber aria-hidden="true" />
        <SheetHead>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SheetTitle>{title}</SheetTitle>
            {subtitle && <SheetSubtitle>{subtitle}</SheetSubtitle>}
          </div>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden="true" />
          </CloseButton>
        </SheetHead>
        <SheetBody>{children}</SheetBody>
        {footer && <SheetFoot>{footer}</SheetFoot>}
      </Sheet>
    </Backdrop>
  );
};

/* ── Product sheet ─────────────────────────────────────────────────────────── */

const FormBar = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: 3px 8px;
  border-radius: ${BorderRadius.sm};
  background: ${(p) => p.$color}1F;
  color: ${(p) => p.$color};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
`;

const Banner = styled.div<{ $tone: 'warn' | 'muted' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  line-height: 1.4;
  ${(p) =>
    p.$tone === 'warn'
      ? css`
          background: rgba(244, 67, 54, 0.1);
          color: ${ThemeColors.error.dark};
        `
      : css`
          background: ${ThemeColors.gray[100]};
          color: ${ThemeColors.gray[800]};
        `}
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${Spacing.xs} ${Spacing.md};
  margin: 0 0 ${Spacing.lg};
  font-size: ${Typography.fontSize.xs};

  dt {
    color: ${ThemeColors.text.hint};
    white-space: nowrap;
  }

  dd {
    margin: 0;
    color: ${ThemeColors.text.primary};
    font-weight: ${Typography.fontWeight.medium};
    word-break: break-word;
  }
`;

const QtySection = styled.div`
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  background: ${ThemeColors.background.default};
`;

const QtyLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const QtyTotal = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const ChipRow = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  flex-wrap: wrap;
  margin-top: ${Spacing.sm};
`;

const Chip = styled.button<{ $active?: boolean }>`
  min-height: 34px;
  padding: 0 ${Spacing.md};
  border: 1px solid
    ${(p) => (p.$active ? ThemeColors.primary.main : ThemeColors.border.main)};
  border-radius: 17px;
  background: ${(p) => (p.$active ? ThemeColors.primary.main : ThemeColors.background.paper)};
  color: ${(p) => (p.$active ? '#fff' : ThemeColors.text.secondary)};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  touch-action: manipulation;
`;

const FacingList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin: ${Spacing.sm} 0 0;
  padding: 0;
`;

const FacingChip = styled.li<{ $current: boolean }>`
  padding: 2px 7px;
  border-radius: ${BorderRadius.sm};
  background: ${(p) => (p.$current ? ThemeColors.primary.main : ThemeColors.gray[200])};
  color: ${(p) => (p.$current ? '#fff' : ThemeColors.text.secondary)};
  font-size: 11px;
  font-weight: ${Typography.fontWeight.semiBold};
  font-variant-numeric: tabular-nums;
`;

const SectionLabel = styled.h3`
  margin: ${Spacing.lg} 0 0;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${ThemeColors.text.hint};
`;

export interface ProductSheetProps {
  product: PlanogramProduct;
  /** Slot the user tapped, used to anchor the position readout. */
  slot: PlanogramSlot;
  /** Every facing this product occupies on the current panel. */
  facings: PlanogramSlot[];
  panelLabel: string;
  displayLabel: string;
  qty: number;
  onChangeQuantity: (qty: number) => void;
  onClose: () => void;
}

export const ProductSheet: React.FC<ProductSheetProps> = ({
  product,
  slot,
  facings,
  panelLabel,
  displayLabel,
  qty,
  onChangeQuantity,
  onClose,
}) => {
  const lineTotal = product.price !== null ? product.price * qty : null;

  return (
    <BottomSheet
      title={product.name}
      subtitle={`${displayLabel} · ${panelLabel} · ${positionLabel(slot)}`}
      onClose={onClose}
      footer={
        <>
          <Chip style={{ flex: 1 }} onClick={() => onChangeQuantity(0)}>
            Clear
          </Chip>
          <Chip $active style={{ flex: 2 }} onClick={onClose}>
            Done
          </Chip>
        </>
      }
    >
      {product.missing && (
        <Banner $tone="muted">
          <HelpCircle size={16} aria-hidden="true" />
          <span>
            This facing has no matching SKU in the portal product list, so it cannot be ordered
            here. It is either a display accessory or needs to be added to the catalogue.
          </span>
        </Banner>
      )}
      {!product.missing && product.outOfStock && (
        <Banner $tone="warn">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>This product is currently marked out of stock in the portal.</span>
        </Banner>
      )}

      <FormBar $color={FORM_COLORS[product.form]}>
        <Tag size={12} aria-hidden="true" />
        {FORM_LABELS[product.form]}
      </FormBar>

      <SectionLabel>Product</SectionLabel>
      <DetailGrid>
        <dt>Shelf label</dt>
        <dd>{product.rackName}</dd>
        {product.nameFr && (
          <>
            <dt>French name</dt>
            <dd>{product.nameFr}</dd>
          </>
        )}
        <dt>Portal item code</dt>
        <dd>{product.itemCode ?? '—'}</dd>
        <dt>Planogram code</dt>
        <dd>{product.rackCode}</dd>
        {product.categoryEn && (
          <>
            <dt>Category</dt>
            <dd>
              {product.categoryEn}
              {product.subCategoryEn ? ` · ${product.subCategoryEn}` : ''}
            </dd>
          </>
        )}
        <dt>Unit price</dt>
        <dd>{formatPrice(product.price)}</dd>
      </DetailGrid>

      <SectionLabel>
        <Layers size={12} aria-hidden="true" style={{ verticalAlign: -1, marginRight: 4 }} />
        Placement on {panelLabel}
      </SectionLabel>
      <p
        style={{
          margin: `${Spacing.xs} 0 0`,
          fontSize: Typography.fontSize.xs,
          color: ThemeColors.text.secondary,
        }}
      >
        <MapPin size={12} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />
        {facings.length === 1
          ? 'Single facing on this panel.'
          : `${facings.length} facings on this panel — order volume should reflect that.`}
      </p>
      <FacingList>
        {facings.map((facing) => (
          <FacingChip
            key={`${facing.row}-${facing.col}`}
            $current={facing.row === slot.row && facing.col === slot.col}
          >
            S{facing.row + 1}·F{facing.col + 1}
          </FacingChip>
        ))}
      </FacingList>

      <SectionLabel>Quantity</SectionLabel>
      <QtySection style={{ marginTop: Spacing.sm }}>
        <QtyLabel>
          <span>Units to order</span>
          {lineTotal !== null && qty > 0 && <QtyTotal>{formatPrice(lineTotal)}</QtyTotal>}
        </QtyLabel>
        <QtyRow>
          <QtyStepper qty={qty} size={48} label={product.name} onChange={onChangeQuantity} />
          <QtyTotal>{qty === 0 ? 'Not in order' : `${qty} unit${qty === 1 ? '' : 's'}`}</QtyTotal>
        </QtyRow>
        <ChipRow>
          {QUICK_QUANTITIES.map((value) => (
            <Chip key={value} $active={qty === value} onClick={() => onChangeQuantity(value)}>
              {value}
            </Chip>
          ))}
          <Chip onClick={() => onChangeQuantity(qty + facings.length)}>
            +{facings.length} (1 per facing)
          </Chip>
        </ChipRow>
      </QtySection>
    </BottomSheet>
  );
};

/* ── Review sheet ──────────────────────────────────────────────────────────── */

export interface OrderLine {
  product: PlanogramProduct;
  qty: number;
}

const LineRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} 0;
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const LineBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const LineName = styled.div`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const LineMeta = styled.div`
  font-size: 11px;
  color: ${ThemeColors.text.hint};
  font-variant-numeric: tabular-nums;
`;

const Totals = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${Spacing.md} 0 0;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
`;

export interface ReviewSheetProps {
  lines: OrderLine[];
  onChangeQuantity: (product: PlanogramProduct, qty: number) => void;
  onClear: () => void;
  onCopy: () => void;
  onDownload: () => void;
  onClose: () => void;
}

export const ReviewSheet: React.FC<ReviewSheetProps> = ({
  lines,
  onChangeQuantity,
  onClear,
  onCopy,
  onDownload,
  onClose,
}) => {
  const units = lines.reduce((sum, line) => sum + line.qty, 0);
  const value = lines.reduce(
    (sum, line) => sum + (line.product.price ?? 0) * line.qty,
    0
  );

  return (
    <BottomSheet
      title="Order draft"
      subtitle={`${lines.length} product${lines.length === 1 ? '' : 's'} · ${units} units`}
      onClose={onClose}
      footer={
        <>
          <Chip onClick={onClear} aria-label="Clear the whole draft">
            <Trash2 size={13} aria-hidden="true" />
          </Chip>
          <Chip style={{ flex: 1 }} onClick={onCopy}>
            <Copy size={13} aria-hidden="true" /> Copy
          </Chip>
          <Chip $active style={{ flex: 1 }} onClick={onDownload}>
            <Download size={13} aria-hidden="true" /> CSV
          </Chip>
        </>
      }
    >
      {lines.length === 0 ? (
        <p style={{ color: ThemeColors.text.hint, fontSize: Typography.fontSize.sm }}>
          Nothing in the draft yet. Tap any facing on the rack to add quantities.
        </p>
      ) : (
        <>
          <Banner $tone="muted">
            <HelpCircle size={16} aria-hidden="true" />
            <span>
              This draft is saved on this device only. Nothing is sent to the portal yet — export
              it while the ordering flow is still in testing.
            </span>
          </Banner>
          {lines.map((line) => (
            <LineRow key={line.product.key}>
              <LineBody>
                <LineName>{line.product.name}</LineName>
                <LineMeta>
                  {line.product.itemCode ?? line.product.rackCode} ·{' '}
                  {formatPrice(line.product.price)}
                  {line.product.price !== null &&
                    ` · ${formatPrice(line.product.price * line.qty)}`}
                </LineMeta>
              </LineBody>
              <QtyStepper
                qty={line.qty}
                size={36}
                label={line.product.name}
                onChange={(next) => onChangeQuantity(line.product, next)}
              />
            </LineRow>
          ))}
          <Totals>
            <span>{units} units</span>
            <span>{formatPrice(value)}</span>
          </Totals>
        </>
      )}
    </BottomSheet>
  );
};
